/**
 * ARTICLES À VALIDER (§6.1bis) — « la caisse dépose, le back-office tranche ».
 *
 * Ce que la page SERT : la file des articles créés en magasin (à la volée pendant une vente, ou
 * préparés hors vente), le RAPPROCHEMENT calculé par le serveur pour chacun, et les trois décisions —
 * valider, fusionner, refuser.
 *
 * ⚠️ TROIS DÉCISIONS, TROIS APPELS, ET C'EST VOULU (le serveur les sépare pour la même raison) : elles
 * n'ont ni les mêmes entrées, ni les mêmes conséquences, ni les mêmes refus.
 *
 * ⚠️ ON NE DÉFAIT JAMAIS UNE VENTE. Un brouillon arrive parfois déjà vendu — `soldCount` le dit, et la
 * page l'affiche. Refuser décide de l'ASSORTIMENT, jamais de l'encaissement : les lignes fiscales
 * portent leur propre copie du libellé et du gencode.
 *
 * ⚠️ CERTITUDE ET HYPOTHÈSE NE SE RENDENT PAS DU MÊME TON. Un rapprochement `certain` (même
 * code-barres) est un fait ; un rapprochement par libellé est une piste. Les afficher pareil ferait
 * fusionner deux articles réels sur une ressemblance de mots — c'est pourquoi seul le premier
 * pré-remplit la cible de fusion.
 */
import { useCallback, useEffect, useId, useMemo, useState } from 'react';
import { confirm, EmptyState, KpiRow, rovingTab, showToast, Trans, useOverlayPanel } from '@orgazone/ui-web';
import { formatMoney, toInputValue } from '@orgazone/money-format';
import { api, type ArticleDraft, type ArticleDraftDuplicate, type ArticleDraftPatch } from '../api';
import { fieldLabel } from '../vocabulary';
import { blockingReason, mergeLabel, validationBlockedReason, defaultDecision, parsePriceCents, prefilledMergeTarget, type DraftDecision } from './draft-decision';
// Un n° de caisse se lit « R01 » partout, jamais « 1 » — la base scelle le code court.
import { codeCaisse } from '../caisse';
import { key, t, type Translatable } from '../i18n';

function Trait({ d, size = 15, stroke = 1.9 }: { d: string; size?: number; stroke?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={stroke} strokeLinecap="round" strokeLinejoin="round"><path d={d} /></svg>
  );
}

const dateCourte = (ms: number): string => {
  const d = new Date(ms);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
};

/** D'où vient le code-barres — et ce que ça change, ce qui est le seul point qui compte ici. */
/**
 * POURQUOI un doublon a été rapproché — le serveur envoie la CLÉ, l'écran dit la phrase.
 *
 * 🔴 LE SERVEUR ENVOYAIT LA PHRASE, et l'infobulle l'affichait telle quelle : un back-office traduit
 *    avec du français dedans, qu'aucune garde du web ne pouvait voir — le texte ne vit pas ici.
 *    C’est le même partage que pour les soldes : la clé voyage, le mot reste du côté qui traduit.
 */
function raisonDuDoublon(code: ArticleDraftDuplicate['whyCode']): string {
  if (code === 'gencode') return t('Le code-barres de ce brouillon est déjà celui de cet article.');
  if (code === 'label-and-price') return t('Libellé très proche et prix quasi identique — probablement le même article.');
  return t('Libellé très proche — à vérifier avant de fusionner.');
}

const SOURCE_CODE: Record<ArticleDraft['gencodeSource'], { l: Translatable; bg: string; fg: string; why: Translatable }> = {
  supplier: {
    l: key('Code fournisseur'), bg: 'var(--teal-100)', fg: 'var(--teal-800)',
    why: key('Identité mondiale, attribuée hors de chez nous : deux caisses qui bippent ce produit désignent bien le même article.'),
  },
  reserve: {
    l: key('Code de réserve'), bg: 'var(--blue-bg)', fg: 'var(--blue-fg)',
    why: key('Code interne pré-alloué à cette caisse — valable, mais il ne dit rien du produit chez les autres.'),
  },
  none: {
    l: key('Sans code'), bg: 'var(--rose-bg)', fg: 'var(--rose-fg)',
    why: key('Aucun code produit : il sera attribué à la validation.'),
  },
};

// ── LA PAGE ────────────────────────────────────────────────────────────────────────────────────
export function AValider() {
  const [rows, setRows] = useState<ArticleDraft[] | null>(null);
  const [motifs, setMotifs] = useState<Record<string, string>>({});
  const [failure, setFailure] = useState<string | null>(null);
  const [origine, setOrigine] = useState<'' | 'sale' | 'menu' | 'mobile-partner'>('');
  const [ouvert, setOuvert] = useState<string | null>(null);

  const reload = useCallback(async () => {
    try {
      setFailure(null);
      const r = await api.articleDrafts();
      setRows(r.rows);
      setMotifs(r.refusalReasons);
    } catch (e) { setFailure(e instanceof Error ? e.message : String(e)); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  const filtres = useMemo(
    () => (rows ?? []).filter((d) => !origine || d.origin === origine),
    [rows, origine],
  );
  const reperes = useMemo(() => {
    const tout = rows ?? [];
    return {
      enAttente: tout.length,
      doublons: tout.filter((d) => d.duplicate?.certain).length,
      vendus: tout.filter((d) => d.soldCount > 0).length,
    };
  }, [rows]);

  const selection = ouvert ? (rows ?? []).find((d) => d.id === ouvert) ?? null : null;

  return (
    <div>
      {/* ⚠️ MARGE DE 16, RELEVÉE DU DESSIN (passe de contrôle 2026-09-07) : elle valait 14, la valeur
          de la MAJORITÉ des écrans — et c'est précisément ce qui la rendait invisible à la relecture.
          Le dessin pose 14, 16 ou 18 selon la page ; on suit le sien, jamais le plus fréquent. */}
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Articles à valider')}</h1>
          <p style={{ margin: '3px 0 0', fontSize: '13.5px', color: 'var(--ink-500)' }}>
            <Trans k='La caisse dépose, le back-office tranche. <1>Refuser décide de l’assortiment, jamais de l’encaissement.</1>' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} />
          </p>
        </div>
      </div>

      {/* ⚠️ `KpiRow` DU KIT — la tuile était recopiée dans sept écrans (2026-09-04). Le TON est un nom
          de famille, plus une chaîne `var(--rose-bg)` avec son repli en dur : la charte
          décide de la teinte, l'écran dit seulement de quoi il parle. */}
      <KpiRow style={{ marginBottom: 12 }} items={[
        { label: t('En attente'), value: String(reperes.enAttente), sub: t('articles créés en magasin, sans décision'), icon: 'M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 7.5V12l3 2', tone: 'amber' },
        { label: t('Doublons certains'), value: String(reperes.doublons), sub: t('même code-barres qu’un article existant — à fusionner'), icon: 'M8 8h12v12H8zM4 16V4h12', tone: 'rose' },
        { label: t('Déjà vendus'), value: String(reperes.vendus), sub: t('des ventes sont encaissées — elles restent, quelle que soit la décision'), icon: 'M6 2l1.5 3h9L18 2M3 6h18l-2 13H5L3 6zM9 11v5M15 11v5', tone: 'blue' },
      ]} />

      <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', background: 'var(--paper)', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid var(--ink-200)', flexWrap: 'wrap' }}>
          <span style={{ display: 'inline-flex', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', background: 'var(--paper)', overflow: 'hidden' }}>
            {([{ v: '', l: t('Tous') }, { v: 'sale', l: t('Créés en vente') }, { v: 'menu', l: t('Préparés') }, { v: 'mobile-partner', l: t('Terminaux') }] as { v: '' | 'sale' | 'menu' | 'mobile-partner'; l: string }[]).map((o) => (
              <button aria-pressed={origine === o.v} key={o.v} onClick={() => setOrigine(o.v)} style={{
                height: 36, padding: '0 12px', border: 'none', cursor: 'pointer', whiteSpace: 'nowrap',
                background: origine === o.v ? 'var(--ink-100)' : 'transparent',
                color: origine === o.v ? 'var(--ink-800)' : 'var(--ink-500)', fontSize: 'var(--t-dense)', fontWeight: 700,
              }}>{o.l}</button>
            ))}
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ fontSize: '11.5px', color: 'var(--ink-500)' }}>{t('{n} en attente', { n: filtres.length })}</span>
        </div>

        {failure ? (
          // ⚠️ Le message RÉEL, jamais un « une erreur est survenue » : c'est lui qui dit quoi chercher.
          <div style={{ padding: '14px 16px', fontSize: '12.5px', color: 'var(--rose-fg)', lineHeight: 1.55 }}>
            {t('La file n’a pas pu être lue — {v}', { v: failure })}
          </div>
        ) : rows === null ? (
          <div style={{ padding: '14px 16px', fontSize: '12.5px', color: 'var(--ink-500)' }}>{t('Lecture de la file…')}</div>
        ) : filtres.length === 0 ? (
          <EmptyState
            icon="tag"
            title={t('Aucun article à valider')}
            note={t('Les articles créés depuis une caisse arrivent ici — pour être validés, fusionnés à un article existant, ou refusés.')}
          />
        ) : (
          <div>
            {filtres.map((d) => {
              const src = SOURCE_CODE[d.gencodeSource];
              return (
                <button key={d.id} onClick={() => setOuvert(d.id)} style={{
                  display: 'flex', alignItems: 'center', gap: 12, width: '100%', padding: '11px 14px',
                  border: 'none', borderTop: '1px solid var(--ink-100)', background: 'var(--paper)',
                  textAlign: 'left', cursor: 'pointer',
                }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
                      <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700, color: 'var(--ink-900)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.name || t('— sans libellé —')}</span>
                      {d.duplicate ? (
                        <span title={raisonDuDoublon(d.duplicate.whyCode)} style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4, height: 19, padding: '0 7px', borderRadius: 'var(--r-xs)', flexShrink: 0,
                          background: d.duplicate.certain ? 'var(--rose-bg)' : 'var(--amber-bg)',
                          color: d.duplicate.certain ? 'var(--rose-fg)' : 'var(--amber-fg)',
                          fontSize: 'var(--t-micro)', fontWeight: 800,
                        }}>{d.duplicate.certain ? t('DOUBLON') : t('PEUT-ÊTRE UN DOUBLON')}</span>
                      ) : null}
                      {d.soldCount > 0 ? (
                        <span title={t('Des ventes sont déjà encaissées sur cet article — elles restent, quelle que soit la décision.')} style={{ display: 'inline-flex', alignItems: 'center', height: 19, padding: '0 7px', borderRadius: 'var(--r-xs)', background: 'var(--blue-bg)', color: 'var(--blue-fg)', fontSize: 'var(--t-micro)', fontWeight: 800, flexShrink: 0 }}>
                          {t('{n} VENDU', { n: d.soldCount })}
                        </span>
                      ) : null}
                    </span>
                    <span style={{ display: 'block', fontSize: '11.5px', color: 'var(--ink-500)', marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {d.gencode ? <code style={{ fontFamily: 'var(--font-mono)' }}>{d.gencode}</code> : t('sans code-barres')}
                      {' · '}{d.origin === 'sale' ? t('créé en vente') : d.origin === 'mobile-partner' ? t('créé au terminal') : t('préparé')}
                      {' · '}{d.origin === 'mobile-partner' ? t('Mobile Partner') : t('caisse {v}', { v: codeCaisse(d.registerId) })}{d.operatorName ? ` — ${d.operatorName}` : ''}
                      {' · '}{dateCourte(d.createdAt)}
                    </span>
                  </span>
                  <span title={t(src.why)} style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 'var(--r-xs)', background: src.bg, color: src.fg, fontSize: '10.5px', fontWeight: 800, flexShrink: 0, whiteSpace: 'nowrap' }}>{t(src.l)}</span>
                  <span style={{ fontSize: 'var(--t-h4)', fontWeight: 800, fontVariantNumeric: 'tabular-nums', color: 'var(--ink-900)', whiteSpace: 'nowrap' }}>{formatMoney(d.salePriceInclTaxCents, { thousandsSep: ' ' })}</span>
                  <span style={{ color: 'var(--ink-400)', display: 'flex', flexShrink: 0 }}><Trait d="M9 6l6 6-6 6" /></span>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {selection ? (
        <ModaleDecision
          draft={selection}
          motifs={motifs}
          onClose={() => setOuvert(null)}
          onDecide={async () => { setOuvert(null); await reload(); }}
        />
      ) : null}
    </div>
  );
}

// ── LA MODALE DE DÉCISION ──────────────────────────────────────────────────────────────────────

/** ⚠️ Définie AU NIVEAU MODULE, jamais pendant un rendu (§24.1) : React démonterait son sous-arbre à
 *  chaque frappe, et le champ en cours de saisie perdrait le focus. */
function ModaleDecision({ draft, motifs, onClose, onDecide }: {
  draft: ArticleDraft;
  motifs: Record<string, string>;
  onClose: () => void;
  onDecide: () => Promise<void>;
}) {
  const empeche = blockingReason(draft);
  // ⚠️ « VALIDER » N'EST PAS OFFERT SUR UN DOUBLON CERTAIN — sans que le brouillon soit pour autant à refuser.
  const valideEmpeche = validationBlockedReason(draft);
  const rattacher = mergeLabel(draft);
  const [onglet, setOnglet] = useState<DraftDecision>(defaultDecision(draft));
  const panelId = useId();
  const titleId = useId();
  // ⚠️ ÉCHAP FERME, ET LE FOCUS ENTRE PUIS REVIENT AU DÉCLENCHEUR : au seul pointeur, la modale ne se quittait pas au clavier.
  const { panelRef } = useOverlayPanel({ open: true, onClose });
  // ⚠️ UNE SEULE LISTE pour les onglets et leurs flèches : ←/→ choisissent la même décision que le clic.
  const decisions: { v: DraftDecision; l: string; off: boolean }[] = [
    { v: 'valider', l: t('Valider'), off: !!valideEmpeche },
    { v: 'fusionner', l: rattacher, off: false },
    { v: 'refuser', l: t('Refuser'), off: false },
  ];
  const [nom, setNom] = useState(draft.name);
  // ⚠️ `toInputValue` et non « ÷ 100, deux décimales, virgule » : l'échelle est celle de la devise du magasin, et la
  //    saisie se relit par la même porte (`parsePriceCents` → `parseMinor`) — l'aller-retour boucle.
  const [prix, setPrix] = useState(toInputValue(draft.salePriceInclTaxCents));
  const [famille, setFamille] = useState(draft.family ?? '');
  const [fournisseur, setFournisseur] = useState(draft.supplier ?? '');
  // ⚠️ Pré-rempli SEULEMENT sur une certitude : une hypothèse pré-remplie se valide sans être relue.
  const [cible, setCible] = useState(prefilledMergeTarget(draft));
  const [motif, setMotif] = useState('');
  const [note, setNote] = useState('');
  const [enCours, setEnCours] = useState(false);

  /** Exécute une décision — l'échec porte le message RÉEL du serveur et ne referme rien (§23.0). */
  const agir = useCallback(async (faire: () => Promise<unknown>, dit: string) => {
    setEnCours(true);
    try {
      await faire();
      showToast(dit, 'ok');
      await onDecide();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err', 7000);
    } finally { setEnCours(false); }
  }, [onDecide]);

  const prixCents = useMemo(() => parsePriceCents(prix), [prix]);

  const valider = useCallback(async () => {
    const patch: ArticleDraftPatch = {
      name: nom.trim(),
      ...(prixCents != null ? { salePriceInclTaxCents: prixCents } : {}),
      ...(famille.trim() ? { family: famille.trim() } : {}),
      ...(fournisseur.trim() ? { supplier: fournisseur.trim() } : {}),
    };
    await agir(() => api.validateArticleDraft(draft.id, patch), t('Article validé — il descendra aux caisses'));
  }, [agir, draft.id, nom, prixCents, famille, fournisseur]);

  const fusionner = useCallback(async () => {
    // ⚠️ CONFIRMATION SUR UNE HYPOTHÈSE SEULEMENT. Sur une certitude (même code-barres) la question
    //    serait du bruit ; sur un rapprochement par libellé, c'est le dernier moment où quelqu'un lit
    //    vraiment vers quoi il fusionne.
    if (!draft.duplicate?.certain) {
      const ok = await confirm({
        title: t('Fusionner sur un rapprochement incertain ?'),
        message: t('Le rapprochement vient du LIBELLÉ, pas du code-barres. Vérifiez que les deux lignes désignent bien le même produit — la fusion ne se défait pas.'),
        confirmLabel: t('Fusionner quand même'),
        tone: 'info',
      });
      if (!ok) return;
    }
    await agir(() => api.mergeArticleDraft(draft.id, cible.trim()), draft.duplicate?.certain && draft.duplicate.archived
      ? t('Remis en service — l’article redescend aux caisses, le brouillon pointe vers lui')
      : t('Fusionné — le brouillon pointe vers l’article conservé'));
  }, [agir, draft.id, draft.duplicate, cible]);

  const refuser = useCallback(
    () => agir(() => api.refuseArticleDraft(draft.id, motif, note.trim() || null), t('Refusé — la caisse cessera de l’offrir')),
    [agir, draft.id, motif, note],
  );

  const src = SOURCE_CODE[draft.gencodeSource];
  const champ: React.CSSProperties = { width: '100%', boxSizing: 'border-box', height: 38, padding: '0 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', fontSize: '12.5px', fontFamily: 'inherit', outline: 'none', background: 'var(--paper)', color: 'var(--ink-900)' };
  const etiquette: React.CSSProperties = { display: 'block', fontSize: 'var(--t-micro)', fontWeight: 800, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)', marginBottom: 5 };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 60, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(12,14,16,.45)' }} />
      <div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby={titleId} style={{ position: 'relative', width: 620, maxWidth: '100%', maxHeight: 'calc(100vh - 40px)', background: 'var(--paper)', borderRadius: 'var(--r-md)', boxShadow: '0 24px 70px rgba(10,12,14,.35)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '15px 20px', borderBottom: '1px solid var(--ink-200)' }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', height: 22, padding: '0 9px', borderRadius: 'var(--r-xs)', background: src.bg, color: src.fg, fontSize: '10.5px', fontWeight: 800 }}>{t(src.l)}</span>
          <span id={titleId} style={{ fontSize: 'var(--t-control)', fontWeight: 700, color: 'var(--ink-900)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {draft.name || t('— sans libellé —')}
          </span>
          <button onClick={onClose} aria-label={t('Fermer')} style={{ width: 32, height: 32, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' }}><Trait d="M6 6l12 12M18 6L6 18" stroke={2.2} /></button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {/* Provenance — qui l'a créé, où, quand, et ce qui s'est déjà vendu dessus. */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 14px', fontSize: '11.5px', color: 'var(--ink-600)', marginBottom: 12 }}>
            <span>{draft.origin === 'sale' ? t('Créé pendant une vente') : draft.origin === 'mobile-partner' ? t('Créé au terminal, sur un gencode inconnu') : t('Préparé hors vente')}</span>
            <span>{draft.origin === 'mobile-partner' ? t('Terminal Mobile Partner') : t('Caisse {v}', { v: codeCaisse(draft.registerId) })}{draft.operatorName ? ` — ${draft.operatorName}` : ''}</span>
            <span>{new Date(draft.createdAt).toLocaleDateString('fr-FR')}</span>
            {draft.gencode ? <span><code style={{ fontFamily: 'var(--font-mono)' }}>{draft.gencode}</code></span> : null}
          </div>
          {draft.note ? (
            <div style={{ fontSize: '12.5px', color: 'var(--ink-700)', padding: '9px 12px', borderRadius: 'var(--r-sm)', background: 'var(--ink-50)', marginBottom: 12, lineHeight: 1.5 }}>
              {t('Note de la caisse : « {v} »', { v: draft.note })}</div>
          ) : null}

          {draft.soldCount > 0 ? (
            // ⚠️ Ce bandeau n'est pas un avertissement décoratif : il retire une inquiétude légitime.
            //    Sans lui, refuser un article vendu quarante fois donne l'impression d'annuler des ventes.
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 13px', borderRadius: 'var(--r-sm)', background: 'var(--blue-bg)', marginBottom: 12 }}>
              <span style={{ display: 'flex', color: 'var(--blue-fg)', paddingTop: 1 }}><Trait d="M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18zM12 8h.01M11 12h1v4h1" stroke={2} /></span>
              <span style={{ fontSize: '12.5px', color: 'var(--blue-fg)', lineHeight: 1.55 }}>
                <Trans
                  k='<1>{n} vente déjà encaissée.</1> Quelle que soit votre décision, elles restent : les lignes fiscales portent leur propre copie du libellé et du code.'
                  vars={{ n: draft.soldCount }} parts={[(x) => <strong>{x}</strong>]} />
              </span>
            </div>
          ) : null}

          {draft.duplicate ? (
            <div style={{
              display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 13px', borderRadius: 'var(--r-sm)', marginBottom: 12,
              background: draft.duplicate.certain ? 'var(--rose-bg)' : 'var(--amber-bg)',
              color: draft.duplicate.certain ? 'var(--rose-fg)' : 'var(--amber-fg)',
            }}>
              <span style={{ display: 'flex', paddingTop: 1 }}><Trait d="M8 8h12v12H8zM4 16V4h12" stroke={2} /></span>
              <span style={{ fontSize: '12.5px', lineHeight: 1.55 }}>
                <strong>{draft.duplicate.certain ? t('Doublon certain') : t('Peut-être un doublon')} — {draft.duplicate.name}</strong>
                {draft.duplicate.gencode ? <> (<code style={{ fontFamily: 'var(--font-mono)' }}>{draft.duplicate.gencode}</code>)</> : null}
                {' · '}{formatMoney(draft.duplicate.salePriceInclTaxCents, { thousandsSep: ' ' })}
                <br />{raisonDuDoublon(draft.duplicate.whyCode)}
                {draft.duplicate.archived ? <><br />{t('Cet article est archivé : le rattacher le remet en service, il redescend en caisse avec son stock et ses photos.')}</> : null}
              </span>
            </div>
          ) : null}

          {empeche ? (
            <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '11px 13px', borderRadius: 'var(--r-sm)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', marginBottom: 12 }}>
              <span style={{ display: 'flex', paddingTop: 1 }}><Trait d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0zM12 9v4M12 17h.01" stroke={2} /></span>
              <span style={{ fontSize: '12.5px', lineHeight: 1.55 }}>{empeche}</span>
            </div>
          ) : null}

          {/* Les trois décisions — onglets, parce qu'elles s'excluent.
              ⚠️ UN VRAI GROUPE D'ONGLETS (rôles et état) : le choix « Fusionner » et l'action « Fusionner » du pied portent le
              même texte — sans rôle, une aide technique annonçait deux boutons identiques. */}
          <div role="tablist" aria-label={t('Décision')} style={{ display: 'inline-flex', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', overflow: 'hidden', marginBottom: 14 }}>
            {decisions.map((o, i) => (
              <button key={o.v} type="button" role="tab" aria-selected={onglet === o.v} disabled={o.off} onClick={() => setOnglet(o.v)}
                id={`${panelId}-tab-${o.v}`} aria-controls={panelId}
                {...rovingTab({ index: i, count: decisions.length, selected: onglet === o.v, onPick: (k) => setOnglet(decisions[k]!.v), disabled: (k) => decisions[k]!.off })}
                title={o.off ? (valideEmpeche ?? undefined) : undefined}
                style={{
                  height: 36, padding: '0 15px', border: 'none', cursor: o.off ? 'not-allowed' : 'pointer',
                  background: onglet === o.v ? 'var(--ink-100)' : 'transparent',
                  color: o.off ? 'var(--ink-300)' : onglet === o.v ? 'var(--ink-800)' : 'var(--ink-500)',
                  fontSize: 'var(--t-dense)', fontWeight: 700,
                }}>{o.l}</button>
            ))}
          </div>

          <div role="tabpanel" id={panelId} aria-labelledby={`${panelId}-tab-${onglet}`}>
          {onglet === 'valider' && !empeche ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <label style={{ gridColumn: '1 / -1' }}><span style={etiquette}>{fieldLabel('name')}</span>
                <input value={nom} onChange={(e) => setNom(e.target.value)} style={champ} /></label>
              <label><span style={etiquette}>{fieldLabel('salePriceInclTax')}</span>
                <input value={prix} onChange={(e) => setPrix(e.target.value)} inputMode="decimal" style={champ} /></label>
              <label><span style={etiquette}>{fieldLabel('family')}</span>
                <input value={famille} onChange={(e) => setFamille(e.target.value)} style={champ} /></label>
              <label style={{ gridColumn: '1 / -1' }}><span style={etiquette}>{fieldLabel('supplier')}</span>
                <input value={fournisseur} onChange={(e) => setFournisseur(e.target.value)} style={champ} /></label>
              <p style={{ gridColumn: '1 / -1', margin: 0, fontSize: '11.5px', color: 'var(--ink-500)', lineHeight: 1.55 }}>
                {draft.gencode
                  ? t('L’article gardera le code-barres du brouillon.')
                  : t('Un code-barres interne sera attribué à la validation — c’est le seul moment où il l’est.')}
              </p>
            </div>
          ) : null}

          {onglet === 'fusionner' ? (
            <div>
              <label><span style={etiquette}>{t('Article conservé')}</span>
                <input value={cible} onChange={(e) => setCible(e.target.value)} placeholder={t('Identifiant de l’article à conserver')} style={champ} /></label>
              <p style={{ margin: '8px 0 0', fontSize: '11.5px', color: 'var(--ink-500)', lineHeight: 1.55 }}>
                
                {t('La fusion ne réécrit rien : le brouillon reste, et la décision dit vers quel article il pointe désormais. Les mouvements de stock et les lignes de vente déjà écrits ne sont pas touchés — ils sont scellés.')}
              </p>
            </div>
          ) : null}

          {onglet === 'refuser' ? (
            <div>
              <span style={etiquette}>{t('Motif — liste fermée')}</span>
              {Object.keys(motifs).length === 0 ? (
                <div style={{ fontSize: '12.5px', color: 'var(--ink-500)', lineHeight: 1.5 }}>
                  
                  {t('Aucun motif reçu du serveur — un refus sans motif ne se compte pas, la décision est donc indisponible.')}
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: 10 }}>
                  {Object.entries(motifs).map(([code, libelle]) => {
                    const on = motif === code;
                    return (
                      <button aria-pressed={on} key={code} onClick={() => setMotif(code)} style={{ height: 36, padding: '0 13px', border: `1.5px solid ${on ? 'var(--teal-800)' : 'var(--ink-200)'}`, borderRadius: 'var(--r-pill)', background: on ? 'var(--teal-800)' : 'var(--paper)', color: on ? '#fff' : 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 700, cursor: 'pointer' }}>{libelle}</button>
                    );
                  })}
                </div>
              )}
              <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('Note (facultatif) — ce que vous avez constaté…')}
                style={{ width: '100%', boxSizing: 'border-box', height: 64, padding: '9px 12px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-ctrl)', fontSize: '12.5px', fontFamily: 'inherit', outline: 'none', resize: 'none', background: 'var(--paper)', color: 'var(--ink-900)' }} />
            </div>
          ) : null}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '13px 20px', borderTop: '1px solid var(--ink-200)', background: 'var(--ink-50)' }}>
          <span style={{ fontSize: '11.5px', color: 'var(--ink-500)', flex: 1 }}>
            
            {t('La décision est tracée — qui, quand, motif. Elle redescend à la caisse.')}
          </span>
          {onglet === 'valider' && !empeche ? (
            <button disabled={enCours || !nom.trim() || prixCents == null} onClick={() => void valider()}
              style={{ height: 38, padding: '0 16px', border: 'none', borderRadius: 'var(--r-ctrl)', background: !enCours && nom.trim() && prixCents != null ? 'var(--teal-800)' : 'var(--ink-300)', color: '#fff', fontSize: 'var(--t-lead)', fontWeight: 700, cursor: enCours ? 'default' : 'pointer' }}>
              
              {t('Valider l’article')}
            </button>
          ) : null}
          {onglet === 'fusionner' ? (
            <button disabled={enCours || !cible.trim()} onClick={() => void fusionner()}
              style={{ height: 38, padding: '0 16px', border: 'none', borderRadius: 'var(--r-ctrl)', background: !enCours && cible.trim() ? 'var(--teal-800)' : 'var(--ink-300)', color: '#fff', fontSize: 'var(--t-lead)', fontWeight: 700, cursor: enCours ? 'default' : 'pointer' }}>
              
              {rattacher}
            </button>
          ) : null}
          {onglet === 'refuser' ? (
            <button disabled={enCours || !motif} onClick={() => void refuser()}
              style={{ height: 38, padding: '0 16px', border: 'none', borderRadius: 'var(--r-ctrl)', background: !enCours && motif ? 'var(--rose-fg)' : 'var(--ink-300)', color: '#fff', fontSize: 'var(--t-lead)', fontWeight: 700, cursor: enCours ? 'default' : 'pointer' }}>
              
              {t('Refuser')}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}
